import { TaskDefinition } from "@core/models/domain/restriction.domain";
import { SuppressedPeriod } from "@core/models/domain/school-calendar.domain";
import { Instant, ZoneId, LocalDate, LocalTime, ZonedDateTime } from "@js-joda/core";
import "@js-joda/timezone"; 

/**
 * Checks if a string is not empty.
 *
 * This function verifies whether the input string is defined, not null, and has a length greater than 0.
 *
 * @param {string | null | undefined} value - The string to be checked.
 * @returns {boolean} True if the string is not empty, otherwise false.
 */
export function isStringNotEmpty(value: string | null | undefined): boolean {
    return value !== undefined && value !== null && value.length > 0;
}

/**
 * Converts a `ZonedDateTime` object to a JavaScript `Date` object.
 *
 * This function removes any `ZoneId` suffix from the `ZonedDateTime` string and converts it to a `Date` object.
 * Returns undefined if the conversion fails.
 *
 * @param {any} zonedDateTime - The `ZonedDateTime` object or string to be converted.
 * @returns {Date | undefined} The converted `Date` object or undefined if the conversion fails.
 */
export function convertZonedDateTimeToDate(zonedDateTime: any): Date {
    if (!zonedDateTime) return undefined;

    let isoString: string =
      typeof zonedDateTime === "string"
        ? zonedDateTime
        : zonedDateTime.toString?.() ?? "";

    // Remove any [ZoneId] suffix (e.g., [Europe/Paris])
    isoString = isoString.replace(/\[.*\]$/, "");

    const date = new Date(isoString);
    return isNaN(date.getTime()) ? undefined : date;
}

/**
 * Converts a JavaScript `Date` object to a `LocalDate` object.
 *
 * This function creates a `LocalDate` object based on the year, month, and day of the input `Date` object.
 * Returns undefined if the input date is null or undefined.
 *
 * @param {Date} date - The `Date` object to be converted.
 * @returns {LocalDate | undefined} The converted `LocalDate` object or undefined if the input date is null or undefined.
 */
export function convertDateToLocalDate(date: Date): LocalDate {
    if (!date) return undefined;
    return LocalDate.of(date.getFullYear(), date.getMonth() + 1, date.getDate());
}

/**
 * Extracts the calendar day of an instant as seen from the given zone.
 *
 * Timestamps sitting on day boundaries (e.g. a validity cutoff at 23:59:59.999 in the
 * task's zone) belong to a different calendar day when extracted in another zone, so
 * the zone that produced the timestamp — not the browser runtime's — must decide which
 * day it falls on.
 *
 * @param {Date} date - The instant to convert.
 * @param {ZoneId} zone - The zone whose calendar day should be extracted.
 * @returns {LocalDate | undefined} The calendar day in that zone, or undefined for a null input.
 */
export function convertDateToLocalDateInZone(date: Date, zone: ZoneId): LocalDate {
    if (!date) return undefined;
    return Instant.ofEpochMilli(date.getTime()).atZone(zone).toLocalDate();
}

/**
 * Converts a JavaScript `Date` object to a `ZonedDateTime` object.
 *
 * This function uses the system's default time zone for the conversion.
 * Returns undefined if the conversion fails.
 *
 * @param {Date} date - The `Date` object to be converted.
 * @returns {ZonedDateTime | undefined} The converted `ZonedDateTime` object or undefined if the conversion fails.
 */
export function convertDateToZonedDatetime(date: Date): ZonedDateTime | undefined {
  if (!date) return undefined;

  try {
    const localDate = LocalDate.of(
      date.getFullYear(),
      date.getMonth() + 1,
      date.getDate()
    );
    const localTime = LocalTime.of(
      date.getHours(),
      date.getMinutes(),
      date.getSeconds()
    );
    const zoneId = ZoneId.systemDefault();

    return ZonedDateTime.of(localDate, localTime, zoneId);
  } catch (error) {
    console.error("Error converting Date to ZonedDateTime:", error);
    return undefined;
  }
}

/**
 * Retrieves the browser's current time zone as a string.
 *
 * This function uses the `Intl.DateTimeFormat` API to determine the browser's current time zone.
 *
 * @returns {string} The browser's current time zone as a string.
 */
export function getTimeZone(): string {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/**
 * Retrieves the browser's current time zone as a `ZoneId` object.
 *
 * This function converts the browser's current time zone string to a `ZoneId` object.
 *
 * @returns {ZoneId} The browser's current time zone as a `ZoneId` object.
 */
export function getBrowserTimeZone(): ZoneId {
    return ZoneId.of(getTimeZone()); 
}   

/**
 * Checks if a task is scheduled on a specific date.
 *
 * This function checks if a task is scheduled on a given date by:
 * 1. If repeatSelectedDays is not empty, it checks if the day matches
 * 2. If repeatSelectedDays is empty, it checks if the date falls within validityStartDate and validityEndDate
 *
 * @param {TaskDefinition} taskDef - The task definition to be checked.
 * @param {LocalDate} date - The date to check against the task's schedule.
 * @returns {boolean} True if the task is scheduled on the given date, otherwise false.
 */
export function isTaskScheduledOnDate(taskDef: TaskDefinition, date: LocalDate): boolean {
    // A day the school calendar suspends for every pupil is still an occurrence of this
    // restriction: the board shows it as "Skipped", with the reason, rather than dropping it (see
    // isFullySuppressedOnDate). Only the cancellations and the schedule itself decide whether the
    // day is an occurrence at all.

    // First check if the date is in cancelledTaskDates
    if (taskDef.cancelledTaskDates && taskDef.cancelledTaskDates.length > 0) {
        const isCancelled = taskDef.cancelledTaskDates.some(cancelledDate => {
            const cancelledLocalDate = convertDateToLocalDate(cancelledDate);
            return cancelledLocalDate.equals(date);
        });
        
        // If the date is cancelled, don't display the task
        if (isCancelled) {
            return false;
        }
    }
    
    const targetDayLabel = toDayLabel(date); 
    
    // If repeatSelectedDays is specified and not empty, check if the day matches
    if (taskDef.repeatSelectedDays && taskDef.repeatSelectedDays.length > 0) {
        const matches = taskDef.repeatSelectedDays.map(day => {
            const dayLabelUpper = day.label.toUpperCase();
            const isMatch = dayLabelUpper === targetDayLabel;
            return isMatch;
        });
        const dayMatches = matches.some(m => m);
        
        // For recurring tasks, also check if the date is within the validity period.
        // The window bounds are day-boundary timestamps in the TASK's zone, so their
        // calendar days must be extracted in that zone: browser-local extraction rolls
        // an end-of-day cutoff onto the next day in runtimes ahead of the task's zone,
        // keeping a replaced series visible on the day its replacement starts.
        // validityEndDate is honoured even without a validityStartDate — a cut-off
        // series may carry only the end (e.g. the optimistic recreate cutoff).
        if (dayMatches && (taskDef.validityStartDate || taskDef.validityEndDate)) {
            const zone = taskDef.timezone ? ZoneId.of(taskDef.timezone) : ZoneId.systemDefault();
            const validityStart = taskDef.validityStartDate
                ? convertDateToLocalDateInZone(new Date(taskDef.validityStartDate), zone)
                : null;
            const validityEnd = taskDef.validityEndDate
                ? convertDateToLocalDateInZone(new Date(taskDef.validityEndDate), zone)
                : null;

            const isAfterStart = !validityStart || date.isEqual(validityStart) || date.isAfter(validityStart);
            const isBeforeEnd = !validityEnd || date.isEqual(validityEnd) || date.isBefore(validityEnd);

            return isAfterStart && isBeforeEnd;
        }

        return dayMatches;
    }
    
    // If repeatSelectedDays is empty, check if the date falls within the validity period
    if (taskDef.validityStartDate) {
        const validityStart = convertDateToLocalDate(new Date(taskDef.validityStartDate));
        const isAfterStart = date.isEqual(validityStart) || date.isAfter(validityStart);

        if (taskDef.validityEndDate) {
            const validityEnd = convertDateToLocalDate(new Date(taskDef.validityEndDate));
            const isBeforeEnd = date.isEqual(validityEnd) || date.isBefore(validityEnd);
            return isAfterStart && isBeforeEnd;
        }

        // No validityEndDate means the task is open-ended — show from start date onward
        return isAfterStart;
    }
    
    return false;
}

/**
 * Converts a `LocalDate` object to a day label string.
 *
 * This function formats the day name with the first letter capitalized and the rest in uppercase.
 *
 * @param {LocalDate} date - The `LocalDate` object to be converted.
 * @returns {string} The formatted day label string.
 */
export function toDayLabel(date: LocalDate): string {
  const dayName = date.dayOfWeek().name(); 
  const result = dayName.charAt(0) + dayName.slice(1).toUpperCase(); 
  return result;
}

/**
 * Retrieves the start of the day for a given `LocalDate`.
 *
 * This function uses the browser's current time zone for the calculation.
 *
 * @param {LocalDate} localDate - The `LocalDate` object representing the day.
 * @returns {ZonedDateTime} The start of the day as a `ZonedDateTime` object.
 */
export function getStartOfDay(localDate: LocalDate): ZonedDateTime {
    return ZonedDateTime.of(localDate, LocalTime.MIDNIGHT, getBrowserTimeZone());
}

/**
 * Retrieves the end of the day for a given `LocalDate`.
 *
 * This function uses the browser's current time zone for the calculation.
 *
 * @param {LocalDate} localDate - The `LocalDate` object representing the day.
 * @returns {ZonedDateTime} The end of the day as a `ZonedDateTime` object.
 */
export function getEndOfDay(localDate: LocalDate): ZonedDateTime {
    return ZonedDateTime.of(localDate, LocalTime.MAX, getBrowserTimeZone());
}

export function getE164PhoneNumber(inputPhoneNumber: string, countryCallingCode: string) {
    if (!inputPhoneNumber) {
        return undefined;
    }
    if (inputPhoneNumber.startsWith("+")) {
        return inputPhoneNumber;
    }
    if (inputPhoneNumber.startsWith("0")) {
        return countryCallingCode + inputPhoneNumber.substring(1, inputPhoneNumber.length);
    }
    return countryCallingCode + inputPhoneNumber;
}

export const MY_DATE_FORMAT = {
  parse: {
    dateInput: 'DD/MM/YYYY',
  },
  display: {
    dateInput: 'DD/MM/YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

  
/**
 * The zone a restriction runs in, falling back to this browser's when it carries none or one the
 * runtime does not know.
 */
export function getTaskZoneId(timezone?: string): ZoneId {
    if (timezone) {
        try {
            return ZoneId.of(timezone);
        } catch (error) {
            console.error("Unknown restriction timezone " + timezone + ", falling back to this browser's", error);
        }
    }
    return ZoneId.systemDefault();
}

/**
 * The instant at which a restriction's own start or end time falls on the given day.
 *
 * <p>Both halves are taken in the restriction's zone: its stored wall-clock time, and the day it
 * is placed on. Reading the hour in the browser's zone instead builds a moment that is out by the
 * offset for anyone viewing from elsewhere, which then decides whether the row counts as skipped.
 * Screens render the result back in the restriction's zone with {@link getTaskZoneOffset}.</p>
 */
export function atDateInTaskZone(date: LocalDate, instant: Date | undefined, timezone?: string): Date | null {
    if (!date || !instant) {
        return null;
    }
    const zone = getTaskZoneId(timezone);
    const taskLocal = Instant.ofEpochMilli(instant.getTime()).atZone(zone);
    const occurrence = ZonedDateTime.of(date, LocalTime.of(taskLocal.hour(), taskLocal.minute()), zone);
    return new Date(occurrence.toInstant().toEpochMilli());
}

/**
 * A stored instant as the wall clock reads in the restriction's zone, carried in a browser-local
 * Date so a form's date picker and hour fields can show it unchanged. The instant is NOT preserved:
 * send it back through {@link convertFormDateToTaskZone}.
 */
export function toTaskZoneWallClock(instant: Date, timezone?: string): Date {
    const local = Instant.ofEpochMilli(instant.getTime()).atZone(getTaskZoneId(timezone));
    return new Date(local.year(), local.monthValue() - 1, local.dayOfMonth(), local.hour(), local.minute(), 0, 0);
}

/**
 * The wall-clock fields of a form Date, built in the browser's zone, read as a time in the
 * restriction's zone. The counterpart of {@link toTaskZoneWallClock}: an edit made from another
 * country keeps the restriction on the hours the school sees.
 */
export function convertFormDateToTaskZone(date: Date, timezone?: string): ZonedDateTime | undefined {
    if (!date) {
        return undefined;
    }
    return ZonedDateTime.of(
        LocalDate.of(date.getFullYear(), date.getMonth() + 1, date.getDate()),
        LocalTime.of(date.getHours(), date.getMinutes()),
        getTaskZoneId(timezone));
}

/** Minutes since midnight in the restriction's zone, for laying its times out on a day axis. */
export function minutesOfDayInTaskZone(instant: Date, timezone?: string): number {
    const local = Instant.ofEpochMilli(instant.getTime()).atZone(getTaskZoneId(timezone));
    return local.hour() * 60 + local.minute();
}

/**
 * The restriction's UTC offset on a given moment, as the `date` pipe wants it ("+0500"), so a time
 * is shown in the zone it runs in rather than the one the browser happens to be in.
 */
export function getTaskZoneOffset(timezone?: string, at?: Date): string {
    const zone = getTaskZoneId(timezone);
    const instant = at ? Instant.ofEpochMilli(at.getTime()) : Instant.now();
    const totalSeconds = zone.rules().offset(instant).totalSeconds();
    const sign = totalSeconds < 0 ? '-' : '+';
    const minutes = Math.abs(Math.trunc(totalSeconds / 60));
    return sign
        + Math.trunc(minutes / 60).toString().padStart(2, '0')
        + (minutes % 60).toString().padStart(2, '0');
}

/**
 * The school calendar's verdict for one restriction on one day, or undefined when that day runs
 * normally.
 *
 * <p>Dates are compared as plain `YYYY-MM-DD` strings in the school's own calendar, never as
 * `Date` objects: converting them would shift a suspension onto the neighbouring day for anyone
 * viewing from a different timezone.</p>
 */
export function getSuppressionForDate(taskDef: TaskDefinition, date: LocalDate): SuppressedPeriod | undefined {
    if (!taskDef?.suppressedPeriods?.length || !date) {
        return undefined;
    }
    const key = date.toString();
    return taskDef.suppressedPeriods.find(period => period.date === key);
}

/**
 * Whether the school calendar suspends this restriction for the whole day and for every pupil.
 *
 * <p>Nothing is launched and no device is restricted on such a day, so the board shows the
 * restriction as skipped rather than as an expected run.</p>
 */
export function isFullySuppressedOnDate(taskDef: TaskDefinition, date: LocalDate): boolean {
    const suppression = getSuppressionForDate(taskDef, date);
    return !!suppression && !!suppression.fullDay && !hasNamedPupils(suppression);
}

/** Whether a suspension names specific pupils rather than covering the whole restriction. */
export function hasNamedPupils(suppression: SuppressedPeriod): boolean {
    return !!suppression.pupilAccountIds && suppression.pupilAccountIds.length > 0;
}

/**
 * A short explanation of why a restriction is suspended or shortened, for the school's own screens:
 * "Half Term", "Early finish (3 pupils)".
 */
export function describeSuppression(suppression: SuppressedPeriod): string {
    if (!suppression) {
        return '';
    }
    const reason = suppression.reasonName || 'School calendar';
    if (hasNamedPupils(suppression)) {
        const count = suppression.pupilAccountIds.length;
        return `${reason} (${count} ${count === 1 ? 'pupil' : 'pupils'})`;
    }
    return reason;
}

/**
 * Date formats for the native date adapter (the moment adapter uses MY_DATE_FORMAT instead).
 * Its values are Intl options, not moment patterns.
 */
export const DD_MM_YYYY_DATE_FORMAT = {
  parse: {
    dateInput: { day: '2-digit', month: '2-digit', year: 'numeric' },
  },
  display: {
    dateInput: { day: '2-digit', month: '2-digit', year: 'numeric' },
    monthYearLabel: { month: 'short', year: 'numeric' },
    dateA11yLabel: { day: '2-digit', month: 'long', year: 'numeric' },
    monthYearA11yLabel: { month: 'long', year: 'numeric' },
  },
};
